import {
  Controller,
  FieldValues,
  FormProvider,
  SubmitHandler,
  UseFormReturn,
} from "react-hook-form";
import { ForwardedRef, forwardRef, useCallback } from "react";

import { Button } from "blocks/components/Button";
import { Field } from "blocks/components/forms/Field";
import { FocusWrapper } from "blocks/components/FocusWrapper";
import { FormContainer } from "blocks/components/forms/FormContainer";
import { FormLabel } from "blocks/components/forms/FormLabel";
import { TextArea } from "blocks/components/forms/TextArea";

import { strings } from "../strings";
import type { ZapbotBlock } from "../types";
import { ZeroState } from "../components/ZeroState";
import { cn } from "utils/cn";
import {
  ALIGNMENT_TO_CLASSNAMES,
  MAX_WIDTH_NAME_TO_CLASSNAMES,
} from "blocks/components/BlockWrapper/constants";

type ZapbotConfig = ZapbotBlock["config"];
type ZapbotStyle = Required<ZapbotConfig>["style"];

type ZapbotProps<T extends FieldValues> = Omit<ZapbotConfig, "style"> &
  ZapbotStyle &
  UseFormReturn<T> & {
    actionIsFocused?: boolean;
    isEditing?: boolean;
    isLoading?: boolean;
    isThumbnail?: boolean;
    inputs?: {
      id: string;
      label: string;
      autoGeneratedId?: boolean | undefined;
    }[];
    output?: string;
    onEnter: React.KeyboardEventHandler<HTMLTextAreaElement>;
    onSubmit: SubmitHandler<T>;
  };

function ZapbotInner<T extends FieldValues>(
  {
    onEnter,
    onSubmit,
    output,
    actionIsFocused = false,
    testBotButtonCopy,
    zapbotId,
    isEditing,
    isLoading,
    isThumbnail,
    inputs = [],
    alignment = "center",
    width = "narrow",
    ...methods
  }: ZapbotProps<T>,
  ref?: ForwardedRef<HTMLFormElement>
) {
  const renderButton = useCallback(
    () => (
      <Button type="submit" isLoading={isLoading}>
        {testBotButtonCopy}
      </Button>
    ),
    [isLoading, testBotButtonCopy]
  );

  if (!zapbotId) {
    return (
      <ZeroState
        title={strings.ZAPBOT_PROMPT_BLOCK_ZERO_STATE_TITLE}
        subtitle={strings.ZAPBOT_PROMPT_BLOCK_ZERO_STATE_DESCRIPTION}
      />
    );
  }

  return (
    <form
      className={cn(
        isEditing
          ? null
          : [
              "w-full",
              ALIGNMENT_TO_CLASSNAMES[alignment],
              MAX_WIDTH_NAME_TO_CLASSNAMES[width],
            ]
      )}
      onSubmit={methods.handleSubmit(onSubmit)}
      ref={ref}
    >
      <FormContainer as="fieldset" disabled={isThumbnail || isLoading}>
        <FormProvider {...methods}>
          {inputs.map((i) => (
            <Controller
              key={i.id}
              name={i.id}
              render={({ field: controllerProps }) => (
                <Field
                  inputId={i.id}
                  label={i.label}
                  isDisabled={isLoading}
                  renderInput={(inputProps) => (
                    <TextArea
                      {...inputProps}
                      {...controllerProps}
                      onKeyDown={onEnter}
                      disableResize
                    />
                  )}
                />
              )}
            />
          ))}
        </FormProvider>
        {isEditing ? (
          <FocusWrapper isFocused={actionIsFocused}>
            {renderButton()}
          </FocusWrapper>
        ) : (
          renderButton()
        )}
        {output !== undefined && (
          <div className="flex flex-col items-stretch gap-2.5">
            <FormLabel size="small">Result</FormLabel>
            <p className="whitespace-pre-wrap rounded-lg bg-zi-primary/5 p-5 text-base font-normal">
              {output}
            </p>
          </div>
        )}
      </FormContainer>
    </form>
  );
}

export const Zapbot = forwardRef(ZapbotInner) as <T extends FieldValues>(
  props: ZapbotProps<T> & { ref?: ForwardedRef<HTMLFormElement> }
) => ReturnType<typeof ZapbotInner>;
